import { Link } from "react-router-dom";
import { MapPin, Mail, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SocialBar } from "@/components/SocialBar";
import { site, navLinks, services, hotlines, mapsUrl } from "@/lib/siteConfig";
import { commonContent } from "@/cms/cmsModel";

export const Footer = () => {
  const copy = commonContent.footer;
  return (
    <footer className="relative overflow-hidden border-t-4 border-[#7A1516] bg-[#E7EDF5]/92" data-testid="site-footer">
      <div className="relative az-container py-14 lg:py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo variant="light" testid="footer-logo-link" />
            <p className="mt-5 max-w-xs text-[#586576]">
              {site.slogan}. {copy.description}
            </p>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-heading text-lg font-semibold text-[#193D6D]">{copy.navigationTitle}</h3>
            <ul className="mt-4 space-y-2.5" data-testid="footer-nav-links">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-[#586576] transition-colors hover:text-[#7A1516]">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-heading text-lg font-semibold text-[#193D6D]">{copy.servicesTitle}</h3>
            <ul className="mt-4 space-y-2.5" data-testid="footer-services-links">
              {services.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/${s.id}`}
                    className="text-[#586576] transition-colors hover:text-[#7A1516]"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-heading text-lg font-semibold text-[#193D6D]">{copy.contactTitle}</h3>
            <ul className="mt-4 space-y-3 text-[#586576]">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 shrink-0 text-[#7A1516]" />
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#7A1516]"
                  data-testid="footer-address-maps-link"
                >
                  {site.address.line1}
                  <br />
                  {site.address.line2}
                  <br />
                  {site.address.region}
                </a>
              </li>
              <li className="group flex items-center gap-3">
                <Mail
                  size={18}
                  className="shrink-0 text-[var(--az-icon-default)] transition-colors group-hover:text-[var(--az-icon-hover)] group-focus-within:text-[var(--az-icon-hover)]"
                />
                <a href={`mailto:${site.email}`} className="transition-colors hover:text-[#7A1516]">
                  {site.email}
                </a>
              </li>
              {hotlines.map((h) => (
                <li key={h.id} className="group flex items-center gap-3">
                  <Phone
                    size={18}
                    className="shrink-0 text-[var(--az-icon-default)] transition-colors group-hover:text-[var(--az-icon-hover)] group-focus-within:text-[var(--az-icon-hover)]"
                  />
                  <a href={`tel:${h.tel}`} className="transition-colors hover:text-[#7A1516]">
                    <span className="text-[#6e7683]">{h.label}:</span> {h.number}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-3.5 border-t border-[#CBD6E2] pt-8" data-testid="footer-social-section">
          <span className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-[#6e7683]">
            {copy.socialTitle}
          </span>
          <SocialBar testidPrefix="footer-social" />
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-3 text-sm text-[#6e7683] sm:flex-row">
          <p>© {new Date().getFullYear()} {site.name}. {copy.rights}</p>
          <p className="font-heading tracking-wide">{site.slogan}</p>
        </div>

        {/* Discreet developer credit */}
        <p className="mt-5 text-center text-[0.6rem] tracking-wide text-[#9aa5b1]/60" data-testid="footer-developer-credit">
          {copy.developer}
        </p>
      </div>
    </footer>
  );
};
